import React from "react";
import { connect } from "react-redux";
import { Button } from "../../Button";
import { Select, Input } from "../../Input";
import { Modal, DeleteModal } from "../../Modal";
import Loading from "../../Loader/Loading";
import toastMessage from "../../../utils/toastMessage";
import { getStorage } from "../../../utils/storage";
import centerQuery from "../../../utils/queries/bloodCenterQuery";
import centerSiteQuery from "../../../utils/queries/bloodCenterSiteQuery";
import fetchAbcPlanning, {
  createAbcPlanning,
  updateAbcPlanning,
  deleteAbcPlanning,
} from "../../../utils/queries/abcPlanningQuery";
import { DEFAULT_MONTHLY_PLAN } from "./abcpConfig";

// ─── Constants ────────────────────────────────────────────────────────────────

const now = new Date();
const CUR_YEAR  = now.getFullYear();
const CUR_MONTH = now.getMonth() + 1;

const YEARS = [0, 1, 2].map(offset => ({
  label: String(CUR_YEAR + offset),
  value: CUR_YEAR + offset,
}));

const MONTHS_ORDER = [
  "JANUARY","FEBRUARY","MARCH","APRIL","MAY","JUNE",
  "JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER",
];

const MONTH_OPTIONS = MONTHS_ORDER.map((m, i) => ({
  label: m.charAt(0) + m.slice(1).toLowerCase(),
  value: i + 1,
}));

const NUM_MONTHS_OPTIONS = [
  { label: "1 month",   value: 1 },
  { label: "2 months",  value: 2 },
  { label: "3 months (quarter)", value: 3 },
  { label: "4 months",  value: 4 },
  { label: "6 months",  value: 6 },
  { label: "12 months (full year)", value: 12 },
  { label: "18 months",             value: 18 },
];

const STATUS_OPTIONS = [
  { label: "Draft",    value: "draft"    },
  { label: "Approved", value: "approved" },
];

const RCBT = [
  { key: "k",  label: "KIGALI",    keywords: ["kigali"]              },
  { key: "b",  label: "BUTARE",    keywords: ["butare","huye"]       },
  { key: "ko", label: "KARONGI",   keywords: ["karongi","kibuye"]    },
  { key: "ru", label: "RUHENGERI", keywords: ["ruhengeri","musanze"] },
  { key: "rw", label: "RWAMAGANA", keywords: ["rwamagana"]           },
];

// For cross-year plans, second-year month keys are prefixed with "Y2_"
// (e.g. "Y2_JANUARY" = January of the year after planning.year).

const emptyMonthPlan = () => [
  { label: "WEEK 1", k:[0,0,0], b:[0,0,0], ko:[0,0,0], ru:[0,0,0], rw:[0,0,0], kNames:[], bNames:[], koNames:[], ruNames:[], rwNames:[], t:[0,0] },
  { label: "WEEK 2", k:[0,0,0], b:[0,0,0], ko:[0,0,0], ru:[0,0,0], rw:[0,0,0], kNames:[], bNames:[], koNames:[], ruNames:[], rwNames:[], t:[0,0] },
  { label: "WEEK 3", k:[0,0,0], b:[0,0,0], ko:[0,0,0], ru:[0,0,0], rw:[0,0,0], kNames:[], bNames:[], koNames:[], ruNames:[], rwNames:[], t:[0,0] },
  { label: "WEEK 4", k:[0,0,0], b:[0,0,0], ko:[0,0,0], ru:[0,0,0], rw:[0,0,0], kNames:[], bNames:[], koNames:[], ruNames:[], rwNames:[], t:[0,0] },
  { label: "WEEK 5", k:[0,0,0], b:[0,0,0], ko:[0,0,0], ru:[0,0,0], rw:[0,0,0], kNames:[], bNames:[], koNames:[], ruNames:[], rwNames:[], t:[0,0] },
  { label: "FIXED",  k:[0,0,0], b:[0,0,0], ko:[0,0,0], ru:[0,0,0], rw:[0,0,0], kNames:[], bNames:[], koNames:[], ruNames:[], rwNames:[], bus:[0,0], t:[0,0] },
];

const buildTemplatePlanData = (startYear, startMonth, endYear, endMonth) => {
  const data = {};
  let y = startYear, m = startMonth;
  while (y < endYear || (y === endYear && m <= endMonth)) {
    const name = MONTHS_ORDER[m - 1];
    const key  = y === startYear ? name : `Y2_${name}`;
    data[key]  = emptyMonthPlan();
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return data;
};

// Strip "Y2_" prefix to get the bare month name used for defaults
const monthBaseName = (key) => key.startsWith("Y2_") ? key.slice(3) : key;

// Normalize per-day center data to [{ name, units }] — module-level so it can be used outside component
const normalizeSites = (d) => {
  if (!d) return [];
  if (d.sites && d.sites.length > 0) return d.sites;
  if (d.groups && d.groups.length > 0) return d.groups.flatMap(g => (g.names || []).map(n => ({ name: n, units: 0 })));
  if (d.names && d.names.length > 0) return d.names.map(n => ({ name: n, units: 0 }));
  return [];
};

// Build a map of { siteName: Date[] } for all planned visits for a given centerKey
// across all months in localData, excluding one specific date (the one being edited).
// Falls back to week-level aggregated names for weeks that haven't been per-day expanded yet.
const buildSiteVisitMap = (localData, centerKey, planning, excludeMonthKey, excludeDayNum) => {
  const map = {};
  Object.entries(localData || {}).forEach(([monthKey, weeks]) => {
    if (!Array.isArray(weeks)) return;
    const isY2 = monthKey.startsWith("Y2_");
    const mIdx = MONTHS_ORDER.indexOf(monthBaseName(monthKey));
    if (mIdx < 0) return;
    const mYear = isY2 ? resolveEndYear(planning) : planning.year;
    weeks.forEach(week => {
      if (week.days) {
        // Expanded week: use precise per-day dates
        week.days.forEach(day => {
          const skip = monthKey === excludeMonthKey && day.date === excludeDayNum;
          if (skip) return;
          const d = day[centerKey];
          if (!d || d.noCollection) return;
          normalizeSites(d).forEach(site => {
            if (!site.name) return;
            if (!map[site.name]) map[site.name] = [];
            map[site.name].push(new Date(mYear, mIdx, day.date));
          });
        });
      } else {
        // Not yet expanded: fall back to week-level aggregated site names.
        // Use mid-week as an approximated date (accurate enough for 60-day check).
        const names = week[`${centerKey}Names`] || [];
        if (names.length === 0) return;
        const wNum = parseInt((week.label || "").replace(/\D/g, "")) || 1;
        const approxDay = Math.min((wNum - 1) * 7 + 4, new Date(mYear, mIdx + 1, 0).getDate());
        const approxDate = new Date(mYear, mIdx, approxDay);
        names.forEach(name => {
          if (!name) return;
          if (!map[name]) map[name] = [];
          map[name].push(approxDate);
        });
      }
    });
  });
  return map;
};

// Derive endYear from API response: use stored value, or detect from Y2_ keys in planData
const resolveEndYear = (planning) => {
  if (planning.endYear) return planning.endYear;
  if (planning.planData && Object.keys(planning.planData).some(k => k.startsWith("Y2_")))
    return planning.year + 1;
  return planning.year;
};

// Generate per-day entries for a week row
const generateWeekDays = (year, monthIdx, weekLabel) => {
  const wNum = parseInt((weekLabel || "").replace(/\D/g, "")) || 1;
  const startDate = (wNum - 1) * 7 + 1;
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const days = [];
  for (let d = startDate; d < startDate + 7 && d <= daysInMonth; d++) {
    const jsDate = new Date(year, monthIdx, d);
    const dayName = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][jsDate.getDay()];
    const entry = { date: d, day: dayName, holiday: "" };
    RCBT.forEach(({ key }) => {
      entry[key] = { noCollection: false, sites: [], sess: 0, units: 0, justification: "" };
    });
    days.push(entry);
  }
  return days;
};

// Recalculate week-level totals from per-day data
const recalcWeekFromDays = (weekRow) => {
  const days = weekRow.days || [];
  const updated = { ...weekRow };
  let totalSess = 0, totalUnits = 0;
  RCBT.forEach(({ key }) => {
    const active = days.filter(d => !d[key]?.noCollection);
    const sess  = active.reduce((s, d) => s + (Number(d[key]?.sess) || 0), 0);
    const units = active.reduce((s, d) => {
      const sts = d[key]?.sites;
      if (sts && sts.length > 0) return s + sts.reduce((a, site) => a + (Number(site.units) || 0), 0);
      // legacy groups
      const grps = d[key]?.groups;
      if (grps && grps.length > 0) return s + grps.reduce((a, g) => a + (Number(g.units) || 0), 0);
      return s + (Number(d[key]?.units) || 0);
    }, 0);
    const names = [...new Set(active.flatMap(d => {
      const sts = d[key]?.sites;
      if (sts && sts.length > 0) return sts.map(site => site.name).filter(Boolean);
      const grps = d[key]?.groups;
      if (grps && grps.length > 0) return grps.flatMap(g => g.names || []);
      return d[key]?.names || [];
    }))];
    updated[key] = [names.length, sess, units];
    updated[`${key}Names`] = names;
    totalSess  += sess;
    totalUnits += units;
  });
  updated.t = [totalSess, totalUnits];
  return updated;
};

// ─── Shared micro-styles ──────────────────────────────────────────────────────

const TH = (bg, color, extra = {}) => ({
  background: bg, color, padding: "4px 6px", border: "1px solid #ccc",
  fontSize: 10, textAlign: "center", whiteSpace: "nowrap", ...extra,
});
const TD = (extra = {}) => ({
  border: "1px solid #dee2e6", padding: "2px 4px", textAlign: "center", ...extra,
});
const subTH = (bg, color, extra = {}) => ({
  background: bg, color, padding: "3px 5px", border: "1px solid #bbb",
  fontSize: 9, textAlign: "center", whiteSpace: "nowrap", ...extra,
});
const subTD = (extra = {}) => ({
  border: "1px solid #dde", padding: "2px 3px", textAlign: "center", fontSize: 9, ...extra,
});
const numInput = (value, onChange, w = 40) => (
  <input
    type="number"
    value={value ?? ""}
    onChange={e => onChange(Number(e.target.value) || 0)}
    style={{ width: w, border: "1px solid #dee2e6", borderRadius: 3, padding: "1px 4px", fontSize: 10, textAlign: "center" }}
  />
);
const smallNum = (value, onChange, w = 32) => (
  <input
    type="number"
    value={value ?? ""}
    onChange={e => onChange(Number(e.target.value) || 0)}
    style={{ width: w, border: "1px solid #dee2e6", borderRadius: 3, padding: "1px 3px", fontSize: 9, textAlign: "center" }}
  />
);

// ─── Planning Inline Editor ───────────────────────────────────────────────────

const PlanningEditor = ({ planning, sitesByRcbt, sitesLoading, onSave, onClose, isSaving, userCenterKey, saveRef }) => {
  // Build month list; supports cross-year plans via "Y2_" prefixed keys
  const months = [];
  {
    const endY = resolveEndYear(planning);
    let y = planning.year, m = planning.startMonth;
    while (y < endY || (y === endY && m <= planning.endMonth)) {
      const name = MONTHS_ORDER[m - 1];
      const key  = y === planning.year ? name : `Y2_${name}`;
      months.push(key);
      m++;
      if (m > 12) { m = 1; y++; }
    }
  }

  // Center-scoped users see only their center; admins see all 5
  const visibleRcbt = userCenterKey ? RCBT.filter(c => c.key === userCenterKey) : RCBT;
  // colSpan for per-day sub-table: Date+Day+Holiday(3) + centers×4 + BUS(admin only,2) + TOTAL(2)
  const totalCols = 3 + visibleRcbt.length * 4 + (!userCenterKey ? 2 : 0) + 2;

  const [localData, setLocalData] = React.useState(() =>
    JSON.parse(JSON.stringify(planning.planData || {}))
  );
  const [selMonth, setSelMonth]     = React.useState(months[0] || "JANUARY");

  // Expose the save function so parent can trigger it from a floating button
  React.useEffect(() => {
    if (saveRef) saveRef.current = () => onSave(planning._id, localData);
  });  // runs after every render to keep localData reference fresh
  const [expandedWeeks, setExpandedWeeks] = React.useState({});
  // Site picker: openPicker = `${ri}-${di}-${centerKey}` or null
  const [openPicker, setOpenPicker] = React.useState(null);
  const [pickerPos, setPickerPos]   = React.useState({ top: 0, left: 0 });
  const [pickerSearch, setPickerSearch] = React.useState("");

  // Close picker on outside click
  React.useEffect(() => {
    if (!openPicker) return;
    const handler = (e) => {
      if (!e.target.closest('[data-mgr-picker]') && !e.target.closest('[data-mgr-picker-btn]')) {
        setOpenPicker(null);
        setPickerSearch("");
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openPicker]);

  const weeks = localData[selMonth] || DEFAULT_MONTHLY_PLAN[monthBaseName(selMonth)] || [];

  // ── Weekly edit functions ─────────────────────────────────────────────────

  const updCell = (ri, centerKey, slotIdx, val) => {
    const nw = weeks.map((w, i) => {
      if (i !== ri) return w;
      const arr = [...(w[centerKey] || [0, 0, 0])];
      arr[slotIdx] = val;
      return { ...w, [centerKey]: arr };
    });
    setLocalData(prev => ({ ...prev, [selMonth]: nw }));
  };

  const updArr = (ri, field, slotIdx, val) => {
    const nw = weeks.map((w, i) => {
      if (i !== ri) return w;
      const arr = [...(w[field] || [0, 0])];
      arr[slotIdx] = val;
      return { ...w, [field]: arr };
    });
    setLocalData(prev => ({ ...prev, [selMonth]: nw }));
  };

  const toggleSite = (ri, centerKey, site) => {
    const nw = weeks.map((w, i) => {
      if (i !== ri) return w;
      const names = [...(w[`${centerKey}Names`] || [])];
      const idx = names.indexOf(site);
      if (idx > -1) names.splice(idx, 1); else names.push(site);
      const arr = [...(w[centerKey] || [0, 0, 0])];
      arr[0] = names.length;
      return { ...w, [centerKey]: arr, [`${centerKey}Names`]: names };
    });
    setLocalData(prev => ({ ...prev, [selMonth]: nw }));
  };

  const updSiteText = (ri, centerKey, val) => {
    const names = val.split(/,\s*/).map(s => s.trim()).filter(Boolean);
    const nw = weeks.map((w, i) => {
      if (i !== ri) return w;
      const arr = [...(w[centerKey] || [0, 0, 0])];
      arr[0] = names.length;
      return { ...w, [centerKey]: arr, [`${centerKey}Names`]: names };
    });
    setLocalData(prev => ({ ...prev, [selMonth]: nw }));
  };

  // ── Per-day edit functions ────────────────────────────────────────────────

  const withDayUpdate = (ri, di, updater) => {
    setLocalData(prev => {
      const monthWeeks = [...(prev[selMonth] || [])];
      const week = { ...monthWeeks[ri] };
      const days = [...(week.days || [])];
      days[di] = updater({ ...days[di] });
      week.days = days;
      monthWeeks[ri] = recalcWeekFromDays(week);
      return { ...prev, [selMonth]: monthWeeks };
    });
  };

  const updDayField = (ri, di, centerKey, field, val) =>
    withDayUpdate(ri, di, day => ({
      ...day, [centerKey]: { ...(day[centerKey] || {}), [field]: val },
    }));

  const toggleDayNoCollection = (ri, di, centerKey) =>
    withDayUpdate(ri, di, day => ({
      ...day, [centerKey]: { ...(day[centerKey] || {}), noCollection: !day[centerKey]?.noCollection },
    }));

  const updDayHoliday = (ri, di, val) => {
    setLocalData(prev => {
      const monthWeeks = [...(prev[selMonth] || [])];
      const week = { ...monthWeeks[ri] };
      const days = [...(week.days || [])];
      days[di] = { ...days[di], holiday: val };
      week.days = days;
      monthWeeks[ri] = week;
      return { ...prev, [selMonth]: monthWeeks };
    });
  };

  // Normalize per-day center data to sites: [{ name, units }] — delegates to module-level normalizeSites
  const getSites = normalizeSites;

  // Toggle a site name: if already in the sites list, remove it; otherwise add with units=0
  const toggleDaySite = (ri, di, centerKey, siteName) =>
    withDayUpdate(ri, di, day => {
      const prev = day[centerKey] || {};
      const sites = getSites(prev);
      const idx = sites.findIndex(s => s.name === siteName);
      const updated = idx > -1
        ? sites.filter((_, i) => i !== idx)
        : [...sites, { name: siteName, units: 0 }];
      const totalUnits = updated.reduce((s, site) => s + (Number(site.units) || 0), 0);
      return { ...day, [centerKey]: { ...prev, sites: updated, units: totalUnits } };
    });

  const removeDaySite = (ri, di, centerKey, si) =>
    withDayUpdate(ri, di, day => {
      const prev = day[centerKey] || {};
      const sites = getSites(prev).filter((_, i) => i !== si);
      const totalUnits = sites.reduce((s, site) => s + (Number(site.units) || 0), 0);
      return { ...day, [centerKey]: { ...prev, sites, units: totalUnits } };
    });

  const updDaySiteUnits = (ri, di, centerKey, si, val) =>
    withDayUpdate(ri, di, day => {
      const prev = day[centerKey] || {};
      const sites = getSites(prev).map((s, i) => i === si ? { ...s, units: Number(val) || 0 } : s);
      const totalUnits = sites.reduce((s, site) => s + (Number(site.units) || 0), 0);
      return { ...day, [centerKey]: { ...prev, sites, units: totalUnits } };
    });

  const updDayJustification = (ri, di, centerKey, val) =>
    withDayUpdate(ri, di, day => ({
      ...day, [centerKey]: { ...(day[centerKey] || {}), justification: val },
    }));

  const isReadOnly = planning.status === "approved" && userCenterKey !== null;

  const toggleExpandWeek = (ri) => {
    const isExpanding = !expandedWeeks[ri];
    if (isExpanding) {
      const week = (localData[selMonth] || [])[ri];
      if (week && !week.days) {
        const monthIdx  = MONTHS_ORDER.indexOf(monthBaseName(selMonth));
        const dayYear   = selMonth.startsWith("Y2_") ? resolveEndYear(planning) : planning.year;
        const days = generateWeekDays(dayYear, monthIdx, week.label);
        setLocalData(ld => ({
          ...ld,
          [selMonth]: (ld[selMonth] || []).map((w, i) => i === ri ? { ...w, days } : w),
        }));
      }
    }
    setExpandedWeeks(prev => ({ ...prev, [ri]: !prev[ri] }));
  };

  // ── Per-day sub-table ─────────────────────────────────────────────────────

  const renderPerDayRows = (ri) => {
    const week = (localData[selMonth] || [])[ri];
    if (!week) return null;
    const days = week.days || [];

    // Compute week's last date for period-end warning
    const lastDay = days.length > 0 ? days[days.length - 1] : null;
    const monthIdx = MONTHS_ORDER.indexOf(monthBaseName(selMonth));
    const weekYear = selMonth.startsWith("Y2_") ? resolveEndYear(planning) : planning.year;
    const daysInMonth = new Date(weekYear, monthIdx + 1, 0).getDate();
    const isLastWeekOfMonth = lastDay && lastDay.date === daysInMonth;

    return (
      <tr key={`days-${ri}`}>
        <td colSpan={totalCols} style={{ padding: 0, background: "#edf4fb", borderLeft: "3px solid #2E75B6" }}>
          <div style={{ padding: "8px 10px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, flexWrap: "wrap", gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: "bold", color: "#1a5276" }}>
                {week.label} — {monthBaseName(selMonth)} per-day plan
                {days.length > 0 && (
                  <span style={{ marginLeft: 8, fontSize: 10, color: "#555", fontWeight: "normal" }}>
                    ({monthBaseName(selMonth)} {days[0].date}–{lastDay?.date})
                  </span>
                )}
              </span>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                {isLastWeekOfMonth && !isReadOnly && (
                  <span style={{ fontSize: 9, padding: "2px 8px", background: "#fff3cd", border: "1px solid #f59e0b", borderRadius: 10, color: "#856404" }}>
                    ⚠ Last week of {monthBaseName(selMonth)} — period ends {lastDay?.date}. Move sites to next month if needed.
                  </span>
                )}
                <span style={{ fontSize: 9, color: "#888" }}>
                  {isReadOnly ? "🔒 Read-only (approved)" : "Edit any cell — changes apply to weekly totals"}
                </span>
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", fontSize: 9, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={subTH("#2E75B6","#fff",{width:24})}>Date</th>
                    <th style={subTH("#2E75B6","#fff",{width:28})}>Day</th>
                    <th style={subTH("#2E75B6","#fff",{minWidth:80})}>Holiday/Note</th>
                    {visibleRcbt.map(c => (
                      <th key={c.key} colSpan={4} style={subTH("#1F4E2D","#fff")}>{c.label}</th>
                    ))}
                  </tr>
                  <tr>
                    <th style={subTH("#FFC000","#000")}></th>
                    <th style={subTH("#FFC000","#000")}></th>
                    <th style={subTH("#FFC000","#000")}></th>
                    {visibleRcbt.map(c => (
                      <React.Fragment key={c.key}>
                        <th style={subTH("#FFC000","#000",{width:20})} title="No collection">🚫</th>
                        <th style={subTH("#FFC000","#000",{minWidth:130,fontSize:8})}>Sites &amp; Units / Justification</th>
                        <th style={subTH("#FFC000","#000",{fontSize:8})}>Sess</th>
                        <th style={subTH("#FFC000","#000",{fontSize:8})}>Units</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {days.map((dayRow, di) => {
                    const isWeekend = dayRow.day === "Sat" || dayRow.day === "Sun";
                    const isPeriodEnd = lastDay && dayRow.date === lastDay.date && !isLastWeekOfMonth;
                    return (
                      <tr key={di} style={{
                        background: isWeekend ? "#fff8e1" : di % 2 === 0 ? "#fff" : "#f5f8ff",
                        borderBottom: (lastDay && dayRow.date === lastDay.date) ? "2px solid #aaa" : undefined,
                      }}>
                        <td style={{ ...subTD(), fontWeight: "bold", textAlign: "center" }}>
                          {dayRow.date}
                          {isPeriodEnd && <span style={{ fontSize: 6, color: "#888", display: "block" }}>end</span>}
                        </td>
                        <td style={{
                          ...subTD(), textAlign: "center",
                          color: isWeekend ? "#c0392b" : "#333",
                          fontWeight: isWeekend ? "bold" : "normal",
                        }}>
                          {dayRow.day}
                        </td>
                        <td style={{ ...subTD(), minWidth: 80 }}>
                          {isReadOnly
                            ? <span style={{ fontSize: 8, color: "#555" }}>{dayRow.holiday || "—"}</span>
                            : <input
                                type="text"
                                value={dayRow.holiday || ""}
                                onChange={e => updDayHoliday(ri, di, e.target.value)}
                                placeholder="—"
                                style={{ width: "100%", border: "1px solid #dee2e6", borderRadius: 3, padding: "1px 4px", fontSize: 8, boxSizing: "border-box" }}
                              />
                          }
                        </td>
                        {visibleRcbt.map(c => {
                          const d = dayRow[c.key] || { noCollection: false, sites: [], sess: 0, units: 0, justification: "" };
                          const apiSites = sitesByRcbt[c.key] || [];
                          const sites = getSites(d);
                          const selectedNames = new Set(sites.map(s => s.name));
                          const totalUnits = sites.reduce((s, site) => s + (Number(site.units) || 0), 0);
                          const pk = `${ri}-${di}-${c.key}`;
                          const isOpen = openPicker === pk;
                          return (
                            <React.Fragment key={c.key}>
                              {/* No-collection toggle */}
                              <td style={{ ...subTD(), textAlign: "center", verticalAlign: "top", paddingTop: 4, background: d.noCollection ? "#f3f4f6" : undefined }}>
                                {isReadOnly
                                  ? (d.noCollection ? <span style={{ fontSize: 9 }}>🚫</span> : null)
                                  : <input
                                      type="checkbox"
                                      checked={!!d.noCollection}
                                      onChange={() => toggleDayNoCollection(ri, di, c.key)}
                                      title="No blood collection"
                                      style={{ cursor: "pointer" }}
                                    />
                                }
                              </td>
                              {/* Sites — each on its own row with its own unit count */}
                              <td style={{ ...subTD(), padding: "3px 4px", verticalAlign: "top", minWidth: 150, background: d.noCollection ? "#f3f4f6" : undefined }}>
                                {d.noCollection ? (
                                  <span style={{ fontSize: 8, color: "#9ca3af", fontStyle: "italic" }}>No collection</span>
                                ) : (
                                  <div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 2, marginBottom: sites.length ? 2 : 0 }}>
                                      {sites.map((site, si) => (
                                        <span key={si} style={{ display: "inline-flex", alignItems: "center", gap: 1, background: "#1F4E2D", color: "#fff", borderRadius: 2, padding: "0px 3px", fontSize: 7 }}>
                                          {site.name}
                                          {isReadOnly
                                            ? (site.units ? <span style={{ opacity: 0.8, marginLeft: 2 }}>{site.units}</span> : null)
                                            : <input type="number" value={site.units ?? ""} onChange={e => updDaySiteUnits(ri, di, c.key, si, e.target.value)}
                                                style={{ width: 28, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: 7, textAlign: "center", borderRadius: 1, padding: "0 1px", marginLeft: 2 }} />
                                          }
                                          {!isReadOnly && (
                                            <button type="button" onClick={() => removeDaySite(ri, di, c.key, si)}
                                              style={{ border: "none", background: "none", color: "#ccc", cursor: "pointer", fontSize: 8, lineHeight: 1, padding: "0 0 0 1px" }}>×</button>
                                          )}
                                        </span>
                                      ))}
                                    </div>
                                    {/* Add site button + picker */}
                                    {!isReadOnly && (
                                      <button type="button" data-mgr-picker-btn="1"
                                        onClick={(e) => {
                                          if (isOpen) { setOpenPicker(null); setPickerSearch(""); return; }
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          const PICKER_H = 320;
                                          const spaceBelow = window.innerHeight - rect.bottom - 8;
                                          const openUp = spaceBelow < PICKER_H && rect.top > PICKER_H;
                                          setPickerPos({ top: openUp ? rect.top - PICKER_H - 4 : rect.bottom + 4, left: Math.min(rect.left, window.innerWidth - 220) });
                                          setPickerSearch(""); setOpenPicker(pk);
                                        }}
                                        style={{ fontSize: 7, padding: "1px 4px", cursor: "pointer", background: "#e9ecef", color: "#555", border: "1px solid #ccc", borderRadius: 2 }}>
                                        + Add site
                                      </button>
                                    )}
                                    {isOpen && (() => {
                                      // 60-day conflict detection
                                      const dayYear = selMonth.startsWith("Y2_") ? resolveEndYear(planning) : planning.year;
                                      const mIdx    = MONTHS_ORDER.indexOf(monthBaseName(selMonth));
                                      const selectedDate = new Date(dayYear, mIdx, dayRow.date);
                                      const visitMap = buildSiteVisitMap(localData, c.key, planning, selMonth, dayRow.date);
                                      const getConflict = (siteName) => {
                                        const dates = visitMap[siteName] || [];
                                        let closest = null;
                                        dates.forEach(d => {
                                          const diffDays = Math.round((d - selectedDate) / 86400000);
                                          if (diffDays !== 0 && Math.abs(diffDays) < 60) {
                                            if (!closest || Math.abs(diffDays) < Math.abs(closest.diffDays))
                                              closest = { date: d, diffDays };
                                          }
                                        });
                                        return closest;
                                      };
                                      const fmtDate = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
                                      const q = pickerSearch.trim().toLowerCase();
                                      const filtered = q ? apiSites.filter(s => s.toLowerCase().includes(q)) : apiSites;
                                      // Annotate with conflict info, then sort: selected first, then clear, then conflicted
                                      const annotated = filtered.map(siteName => ({
                                        siteName,
                                        sel: selectedNames.has(siteName),
                                        conflict: getConflict(siteName),
                                      }));
                                      annotated.sort((a, b) => {
                                        if (a.sel !== b.sel) return a.sel ? -1 : 1;
                                        if (!!a.conflict !== !!b.conflict) return a.conflict ? 1 : -1;
                                        return 0;
                                      });
                                      const conflictCount = annotated.filter(x => x.conflict && !x.sel).length;
                                      return (
                                        <div data-mgr-picker="1" style={{ position: "fixed", zIndex: 9999, top: pickerPos.top, left: pickerPos.left, background: "#fff", border: "1px solid #2E75B6", borderRadius: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.22)", padding: 8, maxHeight: 360, display: "flex", flexDirection: "column", minWidth: 260 }}>
                                          <div style={{ position: "sticky", top: 0, background: "#fff", paddingBottom: 6, borderBottom: "1px solid #eee", marginBottom: 4 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <strong style={{ fontSize: 10, color: "#1F4E2D" }}>{c.label} — {fmtDate(selectedDate)}</strong>
                                                {conflictCount > 0 && (
                                                  <span style={{ fontSize: 8, background: "#fff3cd", color: "#856404", border: "1px solid #f59e0b", borderRadius: 10, padding: "1px 6px" }}>
                                                    ⚠ {conflictCount} within 60d
                                                  </span>
                                                )}
                                              </div>
                                              <button type="button" onClick={() => { setOpenPicker(null); setPickerSearch(""); }} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 14, color: "#888", lineHeight: 1 }}>×</button>
                                            </div>
                                            <input data-mgr-picker="1" type="text" placeholder="Search sites…" value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} autoFocus
                                              style={{ width: "100%", border: "1px solid #ccc", borderRadius: 3, padding: "4px 7px", fontSize: 10, outline: "none", boxSizing: "border-box" }} />
                                          </div>
                                          <div style={{ overflowY: "auto", flex: 1 }}>
                                            {apiSites.length === 0 && <span style={{ fontSize: 9, color: "#aaa" }}>{sitesLoading ? "Loading..." : "No sites found"}</span>}
                                            {filtered.length === 0 && q
                                              ? <span style={{ fontSize: 9, color: "#aaa", padding: "4px 0", display: "block" }}>No results for "{pickerSearch}"</span>
                                              : annotated.map(({ siteName, sel, conflict }) => (
                                                  <label key={siteName} style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: "5px 4px", cursor: "pointer", fontSize: 10, borderBottom: "1px solid #f3f4f6", borderRadius: 3, background: sel ? "#f0f9f4" : conflict ? "#fff8f0" : undefined }}>
                                                    <input type="checkbox" checked={sel} onChange={() => toggleDaySite(ri, di, c.key, siteName)}
                                                      style={{ cursor: "pointer", accentColor: "#1F4E2D", marginTop: 2, flexShrink: 0 }} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                      <span style={{ color: sel ? "#1F4E2D" : conflict ? "#7d3c00" : "#333", fontWeight: sel ? "bold" : "normal" }}>{siteName}</span>
                                                      {conflict && (
                                                        <div style={{ fontSize: 8, color: "#c0392b", marginTop: 1 }}>
                                                          ⚠ also planned {Math.abs(conflict.diffDays)}d {conflict.diffDays > 0 ? "later" : "earlier"} ({fmtDate(conflict.date)})
                                                        </div>
                                                      )}
                                                    </div>
                                                  </label>
                                                ))
                                            }
                                            {conflictCount > 0 && (
                                              <div style={{ fontSize: 8, color: "#888", padding: "6px 4px 2px", borderTop: "1px solid #f0f0f0", marginTop: 2 }}>
                                                ⚠ = this site has another visit within 60 days
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                    {/* Justification field */}
                                    <div style={{ marginTop: 3 }}>
                                      {isReadOnly ? (
                                        d.justification ? (
                                          <div style={{ fontSize: 7, color: "#856404", background: "#fff8e1", padding: "2px 4px", borderRadius: 2, border: "1px solid #f59e0b" }}>
                                            ℹ {d.justification}
                                          </div>
                                        ) : null
                                      ) : (
                                        <input type="text" value={d.justification || ""} onChange={e => updDayJustification(ri, di, c.key, e.target.value)}
                                          placeholder="Justification if plan deviated…"
                                          style={{ width: "100%", border: "1px dashed #bbb", borderRadius: 2, padding: "1px 3px", fontSize: 7, color: "#666", boxSizing: "border-box" }} />
                                      )}
                                    </div>
                                  </div>
                                )}
                              </td>
                              {/* Sessions — single total for the day */}
                              <td style={subTD()}>
                                {d.noCollection
                                  ? <span style={{ color: "#ccc" }}>—</span>
                                  : isReadOnly
                                    ? <span style={{ fontSize: 9 }}>{d.sess ?? 0}</span>
                                    : smallNum(d.sess ?? 0, v => updDayField(ri, di, c.key, "sess", v))
                                }
                              </td>
                              {/* Units — auto-sum of all site units */}
                              <td style={{ ...subTD(), background: "#f0fff4" }}>
                                {d.noCollection
                                  ? <span style={{ color: "#ccc" }}>—</span>
                                  : <span style={{ fontSize: 9, color: "#1F4E2D", fontWeight: "bold" }}>{totalUnits || 0}</span>
                                }
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const monthAbbr = (key) => {
    if (key.startsWith("Y2_")) {
      const yr = String(resolveEndYear(planning)).slice(2);
      return key.slice(3, 6) + "'" + yr;
    }
    return key.slice(0, 3);
  };

  return (
    <div style={{ border: "1px solid #2E75B6", borderRadius: 8, padding: 16, marginTop: 12, background: "#f5f9ff" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <span style={{ fontWeight: "bold", fontSize: 14, color: "#1F4E2D" }}>
            {planning.name || `Planning ${planning.year}`}
          </span>
          <span style={{ marginLeft: 10, fontSize: 12, color: "#555" }}>
            {(() => {
              const ey = resolveEndYear(planning);
              return ey !== planning.year
                ? `${MONTHS_ORDER[planning.startMonth - 1]} ${planning.year} – ${MONTHS_ORDER[planning.endMonth - 1]} ${ey}`
                : `${MONTHS_ORDER[planning.startMonth - 1]} – ${MONTHS_ORDER[planning.endMonth - 1]} ${planning.year}`;
            })()}
          </span>
          <span style={{
            marginLeft: 10, fontSize: 10, padding: "2px 8px", borderRadius: 10,
            background: planning.status === "approved" ? "#d5f5e3" : "#fff3cd",
            color: planning.status === "approved" ? "#155724" : "#856404",
          }}>
            {planning.status || "draft"}
          </span>
        </div>
        <button
          onClick={onClose}
          style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#666", lineHeight: 1 }}
          title="Close editor"
        >×</button>
      </div>

      {/* Month tabs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 14 }}>
        {months.map(m => (
          <button
            key={m}
            onClick={() => { setSelMonth(m); setExpandedWeeks({}); }}
            style={{
              padding: "4px 12px", fontSize: 11, cursor: "pointer", borderRadius: 4,
              fontWeight: selMonth === m ? "bold" : "normal",
              border: selMonth === m ? "2px solid #1F4E2D" : "1px solid #ccc",
              background: selMonth === m ? "#1F4E2D" : "#fff",
              color: selMonth === m ? "#fff" : "#333",
            }}
          >{monthAbbr(m)}</button>
        ))}
      </div>

      {isReadOnly && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", background: "#fff3cd", border: "1px solid #f59e0b", borderRadius: 6, marginBottom: 10, fontSize: 11 }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>🔒</span>
          <div>
            <strong style={{ color: "#856404" }}>This planning is approved and locked.</strong>
            <span style={{ color: "#856404", marginLeft: 6 }}>You are in read-only mode. An admin must set the status back to Draft before editing is allowed.</span>
          </div>
        </div>
      )}

      {sitesLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Loading className="" />
          <span style={{ fontSize: 11, color: "#888" }}>Loading sites...</span>
        </div>
      )}

      {userCenterKey && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 8, padding: "4px 12px", background: "#e8f4fd", border: "1px solid #2E75B6", borderRadius: 20, fontSize: 11 }}>
          <span style={{ color: "#2E75B6", fontWeight: "bold" }}>
            {RCBT.find(c => c.key === userCenterKey)?.label} center view
          </span>
          <span style={{ color: "#555" }}>— you can only edit your center's data</span>
        </div>
      )}

      <p style={{ fontSize: 11, color: "#666", margin: "0 0 8px 0" }}>
        {monthBaseName(selMonth)} — Click <strong>▶</strong> on a WEEK row to expand and edit per-day planned sites, sessions, and units.
      </p>

      {/* Planning table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 10, width: "100%" }}>
          <thead>
            <tr>
              <th style={TH("#1F4E2D", "#fff", { width: 70 })}>Week</th>
              {visibleRcbt.map(c => (
                <th key={c.key} colSpan={3} style={TH("#1F4E2D", "#fff")}>{c.label}</th>
              ))}
              {!userCenterKey && <th colSpan={2} style={TH("#375623", "#fff")}>BUS</th>}
              <th colSpan={2} style={TH("#C55A11", "#fff")}>TOTAL</th>
            </tr>
            <tr>
              <th style={TH("#FFC000", "#000")}></th>
              {visibleRcbt.map(c => (
                <React.Fragment key={c.key}>
                  <th style={TH("#FFC000", "#000", { minWidth: 110, fontSize: 9 })}>Sites</th>
                  <th style={TH("#FFC000", "#000", { fontSize: 9 })}>Sess</th>
                  <th style={TH("#FFC000", "#000", { fontSize: 9 })}>Units</th>
                </React.Fragment>
              ))}
              {!userCenterKey && <>
                <th style={TH("#FFC000", "#000", { fontSize: 9 })}>Sess</th>
                <th style={TH("#FFC000", "#000", { fontSize: 9 })}>Units</th>
              </>}
              <th style={TH("#FFC000", "#000", { fontSize: 9 })}>Sess</th>
              <th style={TH("#FFC000", "#000", { fontSize: 9 })}>Units</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((row, ri) => {
              const isFixed   = row.label === "FIXED";
              const isExpanded = !isFixed && !!expandedWeeks[ri];
              return (
                <React.Fragment key={ri}>
                  <tr style={{ background: isFixed ? "#E2EFDA" : isExpanded ? "#dbeafe" : ri % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                    <td style={{ ...TD(), fontWeight: "bold", fontSize: 9, whiteSpace: "nowrap" }}>
                      {!isFixed && (
                        <button
                          type="button"
                          onClick={() => toggleExpandWeek(ri)}
                          title={isExpanded ? "Collapse per-day view" : "Expand per-day view"}
                          style={{
                            background: "none", border: "none", cursor: "pointer",
                            fontSize: 9, marginRight: 3, color: "#2E75B6", padding: 0,
                            verticalAlign: "middle",
                          }}
                        >{isExpanded ? "▼" : "▶"}</button>
                      )}
                      {row.label}
                    </td>
                    {visibleRcbt.map(c => (
                      <React.Fragment key={c.key}>
                        <td style={TD()}>{isReadOnly ? <span style={{fontSize:10}}>{row[c.key]?.[0] ?? 0}</span> : numInput(row[c.key]?.[0] ?? 0, v => updCell(ri, c.key, 0, v))}</td>
                        <td style={TD()}>{isReadOnly ? <span style={{fontSize:10}}>{row[c.key]?.[1] ?? 0}</span> : numInput(row[c.key]?.[1] ?? 0, v => updCell(ri, c.key, 1, v))}</td>
                        <td style={TD()}>{isReadOnly ? <span style={{fontSize:10}}>{row[c.key]?.[2] ?? 0}</span> : numInput(row[c.key]?.[2] ?? 0, v => updCell(ri, c.key, 2, v))}</td>
                      </React.Fragment>
                    ))}
                    {!userCenterKey && (isFixed ? (
                      <>
                        <td style={TD()}>{isReadOnly ? <span style={{fontSize:10}}>{row.bus?.[0] ?? 0}</span> : numInput(row.bus?.[0] ?? 0, v => updArr(ri, "bus", 0, v))}</td>
                        <td style={TD()}>{isReadOnly ? <span style={{fontSize:10}}>{row.bus?.[1] ?? 0}</span> : numInput(row.bus?.[1] ?? 0, v => updArr(ri, "bus", 1, v))}</td>
                      </>
                    ) : (
                      <>
                        <td style={{ ...TD(), background: "#f0f0f0", color: "#aaa" }}>—</td>
                        <td style={{ ...TD(), background: "#f0f0f0", color: "#aaa" }}>—</td>
                      </>
                    ))}
                    <td style={TD()}>{isReadOnly ? <span style={{fontSize:10}}>{row.t?.[0] ?? 0}</span> : numInput(row.t?.[0] ?? 0, v => updArr(ri, "t", 0, v))}</td>
                    <td style={TD()}>{isReadOnly ? <span style={{fontSize:10}}>{row.t?.[1] ?? 0}</span> : numInput(row.t?.[1] ?? 0, v => updArr(ri, "t", 1, v))}</td>
                  </tr>
                  {isExpanded && renderPerDayRows(ri)}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

class ABCPlanningManager extends React.Component {
  editorSaveRef = { current: null };

  state = {
    plannings:        [],
    loadingPlannings: false,
    filterYear:       CUR_YEAR,

    showCreateModal:  false,
    createName:       "",
    createYear:       CUR_YEAR,
    createStartMonth: CUR_MONTH,
    createNumMonths:  3,
    isCreating:       false,

    editingPlanning:  null,
    isSavingPlanning: false,

    showDeleteModal:  false,
    deleteId:         null,
    isDeleting:       false,

    sitesByRcbt:   { k: [], b: [], ko: [], ru: [], rw: [] },
    sitesLoading:  false,
    userCenterKey: null,  // null = admin (all centers visible)
    showGuide:     false,
  };

  async componentDidMount() {
    this.fetchPlannings();
    this.loadAllSites();
  }

  fetchPlannings = async (year) => {
    const filterYear = year ?? this.state.filterYear;
    this.setState({ loadingPlannings: true });
    try {
      const params = { limit: 100, year: filterYear };
      const res = await fetchAbcPlanning(this.props.defaultLanguage, params);
      const list = (Array.isArray(res) ? res : res?.data) || [];
      this.setState({ plannings: list });
    } catch (_) {}
    this.setState({ loadingPlannings: false });
  };

  handleFilterYear = (y) => {
    this.setState({ filterYear: y, editingPlanning: null }, () => this.fetchPlannings(y));
  };

  loadAllSites = async () => {
    this.setState({ sitesLoading: true });
    try {
      const user = await getStorage();
      const userCenterIds = (user.center || [])
        .map(c => String(c._id || c))
        .filter(Boolean);

      const centers = await centerQuery(this.props.defaultLanguage, { type: "dropdown" });
      const rcbtIds = {};
      RCBT.forEach(({ key, keywords }) => {
        const match = (centers || []).find(c =>
          keywords.some(kw => (c.label || "").toLowerCase().includes(kw))
        );
        if (match) rcbtIds[key] = match.value;
      });

      // Determine which RCBT center this user belongs to (null = admin, sees all)
      let userCenterKey = null;
      if (userCenterIds.length > 0) {
        const matched = Object.entries(rcbtIds).find(([, id]) =>
          userCenterIds.includes(String(id))
        );
        if (matched) userCenterKey = matched[0];
      }

      const results = await Promise.all(
        RCBT.map(async ({ key }) => {
          if (!rcbtIds[key]) return { key, sites: [] };
          const data = await centerSiteQuery(this.props.defaultLanguage, {
            type: "dropdown",
            center: rcbtIds[key],
            limit: 400,
          });
          const names = (data || []).map(s =>
            s.name || (s.label || "").replace(/^[A-Z0-9-]+ - /, "")
          );
          return { key, sites: names };
        })
      );

      const sitesByRcbt = {};
      results.forEach(({ key, sites }) => { sitesByRcbt[key] = sites; });
      this.setState({ sitesByRcbt, userCenterKey });
    } catch (_) {}
    this.setState({ sitesLoading: false });
  };

  openCreate  = () => this.setState({ showCreateModal: true, createYear: this.state.filterYear });
  closeCreate = () => this.setState({ showCreateModal: false, createName: "", createYear: this.state.filterYear, createStartMonth: CUR_MONTH, createNumMonths: 3 });

  handleCreate = async () => {
    const { createName, createYear, createStartMonth, createNumMonths } = this.state;
    const totalMonth = createStartMonth + createNumMonths - 1;
    const endYear    = createYear + Math.floor((totalMonth - 1) / 12);
    const endMonth   = ((totalMonth - 1) % 12) + 1;
    const planData   = buildTemplatePlanData(createYear, createStartMonth, endYear, endMonth);

    this.setState({ isCreating: true });
    try {
      const yearRange = endYear !== createYear ? `${createYear}–${endYear}` : String(createYear);
      await createAbcPlanning(this.props.defaultLanguage, {
        name:       createName || `Planning ${yearRange} – ${MONTHS_ORDER[createStartMonth - 1]} to ${MONTHS_ORDER[endMonth - 1]}`,
        year:       createYear,
        endYear,
        startMonth: createStartMonth,
        endMonth,
        status:     "draft",
        planData,
      });
      toastMessage("success", "ABC Planning created");
      window.dispatchEvent(new Event('abcp-planning-updated'));
      this.closeCreate();
      this.fetchPlannings();
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || "Create failed");
    }
    this.setState({ isCreating: false });
  };

  openEditor  = (planning) => this.setState({ editingPlanning: planning });
  closeEditor = ()         => this.setState({ editingPlanning: null });

  savePlanningData = async (id, planData) => {
    this.setState({ isSavingPlanning: true });
    try {
      const res = await updateAbcPlanning(this.props.defaultLanguage, { id, planData });
      const updated = res?.data || res;
      this.setState(prev => ({
        plannings: prev.plannings.map(p => p._id === id ? { ...p, planData: updated.planData || planData } : p),
        editingPlanning: prev.editingPlanning ? { ...prev.editingPlanning, planData } : null,
      }));
      toastMessage("success", "Planning saved");
      window.dispatchEvent(new Event('abcp-planning-updated'));
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || "Save failed");
    }
    this.setState({ isSavingPlanning: false });
  };

  changeStatus = async (id, status) => {
    try {
      await updateAbcPlanning(this.props.defaultLanguage, { id, status });
      this.setState(prev => ({
        plannings: prev.plannings.map(p => p._id === id ? { ...p, status } : p),
        editingPlanning: prev.editingPlanning?._id === id ? { ...prev.editingPlanning, status } : prev.editingPlanning,
      }));
    } catch {
      toastMessage("error", "Status update failed");
    }
  };

  confirmDelete = (id) => this.setState({ showDeleteModal: true, deleteId: id });
  closeDelete   = ()   => this.setState({ showDeleteModal: false, deleteId: null });

  handleDelete = async () => {
    this.setState({ isDeleting: true });
    try {
      await deleteAbcPlanning(this.props.defaultLanguage, this.state.deleteId);
      toastMessage("success", "Planning deleted");
      this.closeDelete();
      if (this.state.editingPlanning?._id === this.state.deleteId) this.closeEditor();
      this.fetchPlannings();
    } catch {
      toastMessage("error", "Delete failed");
    }
    this.setState({ isDeleting: false });
  };

  render() {
    const {
      plannings, loadingPlannings, filterYear,
      showCreateModal, createName, createYear, createStartMonth, createNumMonths, isCreating,
      editingPlanning, isSavingPlanning,
      showDeleteModal, isDeleting,
      sitesByRcbt, sitesLoading, userCenterKey,
    } = this.state;

    const _totalMonth    = createStartMonth + createNumMonths - 1;
    const createEndYear  = createYear + Math.floor((_totalMonth - 1) / 12);
    const createEndMonth = ((_totalMonth - 1) % 12) + 1;
    const yearList = [CUR_YEAR, CUR_YEAR + 1, CUR_YEAR + 2];

    return (
      <div>
        {/* Header bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <div>
            <h5 style={{ margin: 0, color: "#1F4E2D" }}>ABC Planning Manager</h5>
            <p style={{ margin: 0, fontSize: 11, color: "#666", marginTop: 3 }}>
              Create and manage quarterly/annual blood collection planning documents stored in BBMS.
            </p>
          </div>
          <Button text="+ Create new ABC Planning" onPress={this.openCreate} />
        </div>

        {/* Year filter toolbar */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, padding: "10px 14px", background: "#f8f9fa", borderRadius: 7, border: "1px solid #e0e0e0", flexWrap: "wrap" }}>
          <label style={{ fontSize: 12, fontWeight: "bold", color: "#333", whiteSpace: "nowrap" }}>Year:</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {yearList.map(y => (
              <button
                key={y}
                onClick={() => this.handleFilterYear(y)}
                style={{
                  padding: "4px 16px", fontSize: 12, cursor: "pointer", borderRadius: 5,
                  fontWeight: filterYear === y ? "bold" : "normal",
                  border: filterYear === y ? "2px solid #1F4E2D" : "1px solid #ccc",
                  background: filterYear === y ? "#1F4E2D" : "#fff",
                  color: filterYear === y ? "#fff" : "#333",
                }}
              >{y}</button>
            ))}
          </div>
          {loadingPlannings && <Loading className="" />}
        </div>

        {/* Planning list */}
        {loadingPlannings && plannings.length === 0 ? (
          <Loading className="fullscreen" />
        ) : !loadingPlannings && plannings.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", background: "#f8f9fa", borderRadius: 8, border: "1px dashed #ccc" }}>
            <div style={{ fontSize: 14, color: "#555", marginBottom: 8 }}>No ABC plannings found for {filterYear}.</div>
            <div style={{ fontSize: 12, color: "#888" }}>Click <strong>+ Create new ABC Planning</strong> to get started.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto", marginBottom: 16 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#1F4E2D", color: "#fff" }}>
                  <th style={listTH()}>Name</th>
                  <th style={listTH()}>Year</th>
                  <th style={listTH()}>Period</th>
                  <th style={listTH()}>Status</th>
                  <th style={listTH()}>Created</th>
                  <th style={listTH()}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {plannings.map((p) => {
                  const isEditing = editingPlanning?._id === p._id;
                  return (
                    <tr key={p._id} style={{ background: isEditing ? "#e8f4fd" : undefined, fontWeight: isEditing ? "bold" : "normal" }}>
                      <td style={listTD()}>{p.name || <em style={{ color: "#888" }}>Unnamed</em>}</td>
                      <td style={{ ...listTD(), textAlign: "center" }}>{p.year}</td>
                      <td style={listTD()}>
                        {(() => {
                          const ey = resolveEndYear(p);
                          return ey !== p.year
                            ? `${MONTHS_ORDER[p.startMonth - 1]} ${p.year} – ${MONTHS_ORDER[p.endMonth - 1]} ${ey}`
                            : `${MONTHS_ORDER[p.startMonth - 1]} – ${MONTHS_ORDER[p.endMonth - 1]} ${p.year}`;
                        })()}
                        <span style={{ marginLeft: 6, fontSize: 11, color: "#888" }}>
                          {(() => { const ey = resolveEndYear(p); const n = (ey - p.year) * 12 + p.endMonth - p.startMonth + 1; return `(${n} month${n !== 1 ? "s" : ""})`; })()}
                        </span>
                      </td>
                      <td style={{ ...listTD(), textAlign: "center" }}>
                        <select
                          value={p.status || "draft"}
                          onChange={e => this.changeStatus(p._id, e.target.value)}
                          style={{
                            border: "1px solid #ccc", borderRadius: 4, padding: "2px 8px", fontSize: 11,
                            background: p.status === "approved" ? "#d5f5e3" : "#fff3cd",
                            color: p.status === "approved" ? "#155724" : "#856404",
                            cursor: "pointer",
                          }}
                        >
                          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                      <td style={{ ...listTD(), color: "#888", fontSize: 11 }}>
                        {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "—"}
                      </td>
                      <td style={{ ...listTD(), textAlign: "center" }}>
                        <button
                          onClick={() => isEditing ? this.closeEditor() : this.openEditor(p)}
                          style={actionBtn(isEditing ? "#555" : "#2E75B6")}
                        >{isEditing ? "Close" : "Edit"}</button>
                        {" "}
                        <button onClick={() => this.confirmDelete(p._id)} style={actionBtn("#c0392b")}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Inline planning editor */}
        {editingPlanning && (
          <PlanningEditor
            planning={editingPlanning}
            sitesByRcbt={sitesByRcbt}
            sitesLoading={sitesLoading}
            onSave={this.savePlanningData}
            onClose={this.closeEditor}
            isSaving={isSavingPlanning}
            userCenterKey={userCenterKey}
            saveRef={this.editorSaveRef}
          />
        )}

        {/* Create modal */}
        <Modal show={showCreateModal} handleClose={this.closeCreate} title="Create new ABC Planning" size="md">
          <div className="modal-body">
            <div className="row">
              <div className="col-md-12">
                <Input
                  label="Planning name (optional)"
                  value={createName}
                  placeholder={`e.g. Q1 ${createYear} Blood Collection Plan`}
                  onChange={e => this.setState({ createName: e.target.value })}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 4 }}>
              <div style={{ flex: "1 1 120px" }}>
                <label style={modalLabelStyle}>Year</label>
                <select value={createYear} onChange={e => this.setState({ createYear: Number(e.target.value) })} style={modalSelectStyle}>
                  {YEARS.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                </select>
              </div>
              <div style={{ flex: "2 1 160px" }}>
                <label style={modalLabelStyle}>Start month</label>
                <select value={createStartMonth} onChange={e => this.setState({ createStartMonth: Number(e.target.value) })} style={modalSelectStyle}>
                  {MONTH_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div style={{ flex: "2 1 200px" }}>
                <label style={modalLabelStyle}>Duration</label>
                <select value={createNumMonths} onChange={e => this.setState({ createNumMonths: Number(e.target.value) })} style={modalSelectStyle}>
                  {NUM_MONTHS_OPTIONS.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginTop: 10, padding: "10px 14px", background: "#e8f4fd", borderRadius: 6, fontSize: 12, color: "#1a5276" }}>
              <strong>Planning period:</strong>{" "}
              {createEndYear !== createYear
                ? `${MONTHS_ORDER[createStartMonth - 1]} ${createYear} – ${MONTHS_ORDER[createEndMonth - 1]} ${createEndYear}`
                : `${MONTHS_ORDER[createStartMonth - 1]} – ${MONTHS_ORDER[createEndMonth - 1]} ${createYear}`}
              <span style={{ marginLeft: 8, color: "#555" }}>({createNumMonths} months)</span>
              <br />
              <span style={{ color: "#555", fontSize: 11 }}>
                All weeks and centers start at zero — fill in your own targets after creation.
              </span>
            </div>
          </div>
          <div className="modal-footer">
            <Button text="Cancel"         onPress={this.closeCreate}  className="btn-default" />
            <Button text="Create Planning" onPress={this.handleCreate} isSubmitting={isCreating} />
          </div>
        </Modal>

        {/* Delete modal */}
        <Modal show={showDeleteModal} handleClose={this.closeDelete} title="Delete Planning">
          <DeleteModal handleCloseModal={this.closeDelete} onDelete={this.handleDelete} isDeleting={isDeleting} />
        </Modal>

        {/* ── Floating action bar: Save Planning (when editing) + help button ── */}
        <div style={{ position: "fixed", bottom: 0, right: 0, margin: "1rem", zIndex: 1050, display: "flex", alignItems: "center", gap: 8 }}>

          {/* Save Planning — only shown when the editor is open and NOT read-only */}
          {editingPlanning && !(editingPlanning.status === "approved" && userCenterKey !== null) && (
            <button
              onClick={() => this.editorSaveRef.current?.()}
              disabled={isSavingPlanning}
              style={{
                backgroundColor: "#1F4E2D", color: "white",
                padding: ".5rem 1.1rem",
                borderRadius: "1.5rem",
                boxShadow: "rgba(0,0,0,0.24) 0px 3px 8px",
                border: "2px solid #1F4E2D",
                cursor: isSavingPlanning ? "wait" : "pointer",
                fontWeight: 700, fontSize: 13,
                opacity: isSavingPlanning ? 0.7 : 1,
              }}
            >
              {isSavingPlanning ? "Saving…" : "💾 Save Planning"}
            </button>
          )}

          {/* Need help — button + anchored popover */}
          <div style={{ position: "relative" }}>
            {this.state.showGuide && (
              <div style={{
                position: "absolute", bottom: 54, right: 0,
                width: 400, maxHeight: "72vh",
                background: "#fff", borderRadius: 10,
                boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
                border: "1px solid #dee2e6",
                display: "flex", flexDirection: "column",
                overflow: "hidden",
              }}>
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #dee2e6", background: "#f8f9fa", borderRadius: "10px 10px 0 0", flexShrink: 0 }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>How to Use ABC Planning Manager</span>
                  <button
                    onClick={() => this.setState({ showGuide: false })}
                    style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#888", lineHeight: 1, padding: 0 }}
                  >×</button>
                </div>
                {/* Scrollable body */}
                <div style={{ overflowY: "auto", padding: "16px", fontSize: 13, lineHeight: 1.65 }}>

                  <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)", fontWeight: 700, marginBottom: 6 }}>What is ABC Planning Manager?</p>
                  <p style={{ marginBottom: 16 }}>
                    The <strong>ABC Planning Manager</strong> lets you create, edit, and manage blood collection planning documents stored in BBMS. Each planning covers a set of months and contains per-week targets for all five blood transfusion centers.
                  </p>

                  <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)", fontWeight: 700, marginBottom: 6 }}>Creating a new planning</p>
                  <ol style={{ paddingLeft: 18, marginBottom: 16 }}>
                    <li>Click <strong>+ Create new ABC Planning</strong> in the top-right corner.</li>
                    <li>Enter a <strong>Name</strong> for the planning (e.g. "2026 Q1 Plan").</li>
                    <li>Select the <strong>Year</strong>, <strong>Start month</strong>, and <strong>Duration</strong> (number of months).</li>
                    <li>Click <strong>Create Planning</strong>. The planning will appear in the list below.</li>
                    <li>Click <strong>Edit</strong> on the new planning to fill in weekly targets for each center.</li>
                  </ol>

                  <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)", fontWeight: 700, marginBottom: 6 }}>Managing plannings</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                    {[
                      { label: "Year filter",   desc: "Use the 2026 / 2027 / 2028 buttons to switch between years and see their plannings." },
                      { label: "Edit",          desc: "Opens the inline editor — set sessions and units per week for each center." },
                      { label: "Delete",        desc: "Permanently removes the planning. This cannot be undone." },
                      { label: "Active status", desc: "Only one planning can be active at a time. The active planning feeds the ABC Planning report." },
                    ].map(({ label, desc }) => (
                      <div key={label} style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: "8px 10px" }}>
                        <strong style={{ display: "block", marginBottom: 3, fontSize: 12 }}>{label}</strong>
                        <span style={{ fontSize: 11, color: "#555" }}>{desc}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ background: "#fff8e1", border: "1px solid #f59e0b", borderRadius: 6, padding: "8px 12px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 15, lineHeight: 1.4 }}>⚠</span>
                    <span style={{ fontSize: 11, color: "#92400e", lineHeight: 1.5 }}>
                      <strong>Note:</strong> Once a planning is marked active, the <em>ABC Planning</em> report will use its targets instead of the default values. Use <em>ABCP Preparation</em> to customize page content (cover, foreword, etc.).
                    </span>
                  </div>

                </div>
              </div>
            )}

            <button
              title="Need help? Use this guide"
              onClick={() => this.setState(prev => ({ showGuide: !prev.showGuide }))}
              style={{
                width: 44, height: 44, borderRadius: "50%",
                backgroundColor: this.state.showGuide ? "#fff" : "var(--primary-color)",
                color: this.state.showGuide ? "var(--primary-color)" : "white",
                border: "2px solid var(--primary-color)",
                boxShadow: "rgba(0,0,0,0.28) 0px 3px 10px",
                cursor: "pointer", fontSize: 22, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                lineHeight: 1,
              }}
            >?</button>
          </div>
        </div>
      </div>
    );
  }
}

// ─── Table cell styles ────────────────────────────────────────────────────────

const listTH = () => ({
  padding: "8px 10px", textAlign: "left", fontWeight: "bold",
  fontSize: 12, border: "1px solid #145a32", whiteSpace: "nowrap",
});

const listTD = () => ({
  padding: "7px 10px", border: "1px solid #ddd", fontSize: 12,
});

const actionBtn = (color) => ({
  background: color, color: "#fff", border: "none",
  borderRadius: 3, padding: "3px 10px", cursor: "pointer", fontSize: 11,
});

const modalLabelStyle = {
  display: "block", fontSize: 12, fontWeight: "600", color: "#333", marginBottom: 4,
};

const modalSelectStyle = {
  width: "100%", border: "1px solid #ccc", borderRadius: 4,
  padding: "7px 10px", fontSize: 13, background: "#fff", cursor: "pointer", outline: "none",
};

const mapStateToProps = state => ({ defaultLanguage: state.Language.defaultLanguage });
export default connect(mapStateToProps)(ABCPlanningManager);
